import useAppStore from '@/store/app.store';
import useSettingsStore from '@/views/settings_view/store/useSettingsStore';
import { useCallback, useEffect, useMemo } from 'react';
import { ACTIVE_COMPANIES_INITIAL_FILTERS } from '../constants/filters';

export const useActiveCompaniesFilters = () => {
  const { masters } = useAppStore();
  const { activeCompaniesFilters, setActiveCompaniesFilters, filterActiveCompanies } = useSettingsStore();

  useEffect(() => {
    if (activeCompaniesFilters.length === 0) {
      setActiveCompaniesFilters(ACTIVE_COMPANIES_INITIAL_FILTERS);
    }
  }, [activeCompaniesFilters, setActiveCompaniesFilters, masters.companies, masters.plants]);

  const updateFilters = useCallback(
    (id: string, value: string) => {
      const newFilters = activeCompaniesFilters.map((filter) => {
        if (filter.accessor === id) {
          return { ...filter, value };
        }
        return filter;
      });

      setActiveCompaniesFilters(newFilters);
      filterActiveCompanies();
    },
    [activeCompaniesFilters, setActiveCompaniesFilters, filterActiveCompanies]
  );

  // Filtered cups options based on selected companies
  const filteredCupsOptions = useMemo(() => {
    const companiesFilter = activeCompaniesFilters.find((filter) => filter.accessor === 'name');
    const newOptions = masters.plants?.filter((plant) => {
      if (companiesFilter?.value && companiesFilter.value.length > 0) {
        const filteredCompanyIds = companiesFilter?.value?.map(
          (selectedCompany: string) => masters.companies?.find((company) => company.name === selectedCompany)?.id
        );
        return filteredCompanyIds.includes(plant.companyId);
      }
      return true;
    });

    return newOptions ?? masters.plants ?? [];
  }, [masters.plants, activeCompaniesFilters, masters.companies]);

  const filtersTable = useMemo(
    () =>
      activeCompaniesFilters.map((filter) => {
        const baseFilter = {
          ...filter,
          onChange: (id: string, value: string) => updateFilters(id, value),
        };

        const filterConfigs = {
          name: {
            options: masters.companies?.map((company) => ({
              label: company.name,
              id: company.name,
            })),
          },
          cups: {
            options: filteredCupsOptions.map((plant) => ({
              label: plant.identifier,
              id: plant.identifier,
            })),
          },
          alias: {
            options: filteredCupsOptions.map((plant) => ({
              label: plant.alias,
              id: plant.alias,
            })),
          },
        };

        return {
          ...baseFilter,
          ...(filterConfigs[filter.accessor as keyof typeof filterConfigs] || {}),
        };
      }),
    [activeCompaniesFilters, updateFilters, masters, filteredCupsOptions]
  );

  return { filtersTable };
};
